import { useState } from 'react';
import {
  Mail,
  RefreshCw,
  CheckCircle2,
  XCircle,
  CircleDot,
  History,
  AlertTriangle,
} from 'lucide-react';
import { EmailLog } from '@/types/email-log.types';
import EmailLogStatusBadge from './EmailLogStatusBadge';
import EmailLogAvatar from './EmailLogAvatar';
import EmptyState from '@/components/ui/EmptyState';
import Modal from '@/components/ui/Modal';

interface EmailLogDetailProps {
  log: EmailLog | null;
  onRetry?: (log: EmailLog) => void;
  isRetrying?: boolean;
}

// Process HTML content to make links open in new tab
function processHtmlContent(htmlContent: string): string {
  let processedContent = htmlContent.replace(
    /<a\s+(?![^>]*target=)/gi,
    '<a target="_blank" rel="noopener noreferrer" '
  );

  processedContent = processedContent.replace(
    /<a\s+([^>]*?)target="[^"]*"/gi,
    '<a $1target="_blank" rel="noopener noreferrer"'
  );

  return processedContent;
}

function formatTimelineDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }) + ', ' + date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

interface TimelineEntry {
  label: string;
  description?: string;
  timestamp?: string;
  status: 'completed' | 'failed' | 'pending';
}

function buildTimelineEntries(log: EmailLog): TimelineEntry[] {
  const entries: TimelineEntry[] = [];

  // 1. Email Created
  entries.push({
    label: 'Email Created',
    timestamp: log.created_at,
    status: 'completed',
  });

  // 2. Send failed
  if (log.failed_at || log.error_message) {
    entries.push({
      label: log.status === 'bounced' ? 'Email Bounced' : 'Send Failed',
      timestamp: log.failed_at,
      status: 'failed',
    });
  }

  // 3. Retry attempts
  if (log.retry_count && log.retry_count > 0) {
    for (let i = 1; i <= log.retry_count; i++) {
      const isLastRetry = i === log.retry_count;
      const retrySucceeded = isLastRetry && (log.status === 'sent' || log.status === 'delivered');

      entries.push({
        label: `Retry ${i}`,
        description: retrySucceeded ? 'Sent successfully' : (isLastRetry && log.status === 'failed' ? 'Retry failed' : 'Retry attempted'),
        timestamp: isLastRetry && retrySucceeded && log.sent_at ? log.sent_at : undefined,
        status: retrySucceeded ? 'completed' : 'failed',
      });
    }
  }

  // 4. Next retry scheduled
  if (log.status === 'retry_scheduled' && log.next_retry_at) {
    entries.push({
      label: 'Next Retry Scheduled',
      description: `Scheduled at ${formatTimelineDate(log.next_retry_at)}`,
      status: 'pending',
    });
  }

  // 5. Email Sent — after retry or direct send
  if (log.sent_at && (log.status === 'sent' || log.status === 'delivered')) {
    const isAfterRetry = !!(log.failed_at || log.retry_count);
    entries.push({
      label: isAfterRetry ? 'Retry Sent' : 'Email Sent',
      description: isAfterRetry ? 'Sent successfully after retry' : undefined,
      timestamp: log.sent_at,
      status: 'completed',
    });
  }

  // 6. Email Delivered
  if (log.delivered_at && log.status === 'delivered') {
    entries.push({
      label: 'Email Delivered',
      timestamp: log.delivered_at,
      status: 'completed',
    });
  }

  // 7. Still pending (no failure, no send yet)
  if (log.status === 'pending' && !log.failed_at && !log.sent_at && !log.error_message) {
    entries.push({
      label: 'Pending',
      description: 'Awaiting send',
      status: 'pending',
    });
  }

  return entries;
}

function getTimelineIcon(status: TimelineEntry['status']) {
  switch (status) {
    case 'completed':
      return <CheckCircle2 className="w-4 h-4 text-green-500" />;
    case 'failed':
      return <XCircle className="w-4 h-4 text-red-500" />;
    case 'pending':
      return <CircleDot className="w-4 h-4 text-yellow-500" />;
  }
}

function getTimelineLineColor(status: TimelineEntry['status']) {
  switch (status) {
    case 'completed':
      return 'bg-green-300';
    case 'failed':
      return 'bg-red-300';
    case 'pending':
      return 'bg-gray-300';
  }
}

function ActivityTimelineContent({ log }: { log: EmailLog }) {
  const entries = buildTimelineEntries(log);

  return (
    <div className="relative">
      {entries.map((entry, index) => {
        const isLast = index === entries.length - 1;

        return (
          <div key={index} className="flex gap-3 relative">
            {/* Line + Icon */}
            <div className="flex flex-col items-center">
              <div className="flex-shrink-0 z-10 bg-white">
                {getTimelineIcon(entry.status)}
              </div>
              {!isLast && (
                <div className={`w-0.5 flex-1 min-h-[24px] ${getTimelineLineColor(entry.status)}`} />
              )}
            </div>

            {/* Content */}
            <div className={isLast ? 'pb-0 min-w-0' : 'pb-4 min-w-0'}>
              <p className="text-sm font-medium text-gray-900 leading-4">{entry.label}</p>
              {entry.timestamp && (
                <p className="text-xs text-gray-500 mt-0.5">{formatTimelineDate(entry.timestamp)}</p>
              )}
              {entry.description && (
                <p className={`text-xs mt-0.5 break-all ${entry.status === 'failed' ? 'text-red-600' : 'text-gray-500'}`}>
                  {entry.description}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function EmailLogDetail({
  log,
  onRetry,
  isRetrying,
}: EmailLogDetailProps) {
  const [showTimeline, setShowTimeline] = useState(false);

  if (!log) {
    return (
      <EmptyState
        icon={Mail}
        title="No Conversation Found"
        description="Select an email from the list to view its details."
      />
    );
  }

  const canRetry =
    log.status === 'failed' || log.status === 'pending' || log.status === 'retry_scheduled' || log.status === 'bounced';

  return (
    <div>
      {/* Header - Name and Status */}
      <div className="p-4 border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="flex items-start gap-3">
          <EmailLogAvatar
            name={log.recipient_name || log.recipient_email}
            email={log.recipient_email}
            size="lg"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-base font-semibold text-gray-900">
                {log.recipient_name || 'No Name'}
              </h2>
              <EmailLogStatusBadge status={log.status} />
              {log.email_type && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600 capitalize">
                  {log.email_type.replace(/_/g, ' ')}
                </span>
              )}
            </div>

            {/* Recipients Section */}
            <div className="space-y-1 text-sm mb-2">
              <div className="flex gap-2">
                <span className="text-gray-500 flex-shrink-0">To:</span>
                <span className="text-gray-700 break-all">{log.recipient_email}</span>
              </div>
              {log.cc_emails && log.cc_emails.length > 0 && (
                <div className="flex gap-2">
                  <span className="text-gray-500 flex-shrink-0">CC:</span>
                  <span className="text-gray-700 break-all">{log.cc_emails.join(', ')}</span>
                </div>
              )}
              {log.approver_emails && log.approver_emails.length > 0 && (
                <div className="flex gap-2">
                  <span className="text-gray-500 flex-shrink-0">Approvers:</span>
                  <span className="text-gray-700 break-all">{log.approver_emails.join(', ')}</span>
                </div>
              )}
            </div>

            {/* Subject */}
            <div className="flex gap-2 text-sm">
              <span className="text-gray-500 flex-shrink-0">Subject:</span>
              <span className="text-gray-900 font-medium">{log.subject}</span>
            </div>
          </div>

          {/* Action Buttons - top right */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setShowTimeline(true)}
              className="p-1.5 text-gray-500 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              title="Activity Log"
            >
              <History className="w-4 h-4" />
            </button>
            {canRetry && onRetry && (
              <button
                onClick={() => onRetry(log)}
                disabled={isRetrying}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isRetrying ? 'animate-spin' : ''}`} />
                {isRetrying ? 'Retrying...' : 'Retry'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error Message Banner */}
      {log.error_message && (log.status === 'failed' || log.status === 'bounced' || log.status === 'pending' || log.status === 'retry_scheduled') && (
        <div className="mx-4 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-600 break-all">{log.error_message}</p>
          </div>
        </div>
      )}

      {/* Activity Log Modal */}
      <Modal
        isOpen={showTimeline}
        onClose={() => setShowTimeline(false)}
        title="Activity Log"
        size="sm"
      >
        <ActivityTimelineContent log={log} />
      </Modal>

      {/* Email Preview */}
      <div className="pt-4">
        {log.html_content ? (
          <div
            className="email-content"
            dangerouslySetInnerHTML={{ __html: processHtmlContent(log.html_content) }}
          />
        ) : (
          <div className="flex items-center justify-center py-32">
            <div className="text-center text-gray-500">
              <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">No email preview available</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
